import * as React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { BarChart3, Link2, QrCode, Users } from 'lucide-react';
export const Route = createFileRoute('/_layout/learn/smart-links-vs-link-in-bio/')({
    component: LearnSmartLinksVsLinkInBioPage,
    head: () => ({
        meta: [
            { title: 'Smart Links vs Link-in-Bio — Key Differences Explained | Adora' },
            {
                name: 'description',
                content: 'Link-in-bio tools group links. Smart Links track where people come from and what they do next. Discover the difference and why it matters for campaigns.',
            },
            { property: 'og:title', content: 'Smart Links vs Link-in-Bio — Key Differences Explained | Adora' },
            {
                property: 'og:description',
                content: 'Link-in-bio tools group links. Smart Links track where people come from and what they do next. Discover the difference and why it matters for campaigns.',
            },
        ],
    }),
});
function LearnSmartLinksVsLinkInBioPage() {
    return (<main className="mx-auto max-w-6xl px-6 md:px-8 py-12">
            {/* HERO */}
            <div className="text-center max-w-3xl mx-auto space-y-3">
                <h1 className="text-3xl md:text-4xl font-bold">Smart Links vs Link-in-Bio</h1>
                <p className="text-muted-foreground">
                    Both share links. Only one explains <em>why</em> people click.
                </p>
            </div>

            <Separator className="my-10"/>

            {/* COMPARISON */}
            <div className="grid gap-6 md:grid-cols-2">
                {/* LINK-IN-BIO */}
                <Card className="h-full">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Link2 className="h-5 w-5 text-muted-foreground"/>
                            Link-in-Bio tools
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm text-muted-foreground">
                        <ul className="space-y-2">
                            <li>• One landing page with many links</li>
                            <li>• Designed for social profiles</li>
                            <li>• Click counts per link</li>
                            <li>• No placement or context awareness</li>
                        </ul>

                        <div className="rounded-md bg-muted/40 p-3">
                            <p className="font-medium text-foreground text-sm mb-1">Best for:</p>
                            <p>Creators, influencers, and profiles that need a single public link.</p>
                        </div>
                    </CardContent>
                </Card>

                {/* SMART LINKS */}
                <Card className="h-full border-primary/30">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <BarChart3 className="h-5 w-5 text-primary"/>
                            Smart Links (Adora)
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm text-muted-foreground">
                        <ul className="space-y-2">
                            <li>• Each link belongs to a campaign</li>
                            <li>• Same destination, different contexts</li>
                            <li>• Tracks where people came from</li>
                            <li>• Designed for comparison & testing</li>
                        </ul>

                        <div className="rounded-md bg-primary/5 p-3">
                            <p className="font-medium text-foreground text-sm mb-1">Best for:</p>
                            <p>Businesses running flyers, packaging, QR codes, menus, or repeated campaigns.</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            <Separator className="my-12"/>

            {/* USE CASE STRIP */}
            <div className="grid gap-6 sm:grid-cols-3">
                <UseCase icon={<QrCode />} title="Offline campaigns" text="Menus, posters, packaging, table tents"/>
                <UseCase icon={<Users />} title="Multiple placements" text="Same offer, different locations or formats"/>
                <UseCase icon={<BarChart3 />} title="Decision making" text="Compare what actually performs"/>
            </div>

            <Separator className="my-12"/>

            {/* CTA */}
            <Card>
                <CardContent className="p-6 md:p-8 flex flex-col md:flex-row gap-6 items-start md:items-center justify-between">
                    <div>
                        <h3 className="text-xl font-semibold">When links need answers, not just clicks</h3>
                        <p className="text-muted-foreground text-sm mt-1 max-w-xl">
                            Smart Links help you understand which ideas, placements, and materials actually work.
                        </p>
                    </div>

                    <div className="flex gap-2">
                        <Button asChild>
                            <Link to="/sign-up" search={{ redirect: '/console' }}>
                                Start free
                            </Link>
                        </Button>
                        <Button asChild variant="outline">
                            <Link to="/learn/smart-links">Learn Smart Links</Link>
                        </Button>
                    </div>
                </CardContent>
            </Card>
        </main>);
}
function UseCase({ icon, title, text }) {
    return (<Card className="h-full">
            <CardContent className="p-5 space-y-2">
                <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                    {icon}
                </div>
                <div className="font-medium">{title}</div>
                <div className="text-sm text-muted-foreground">{text}</div>
            </CardContent>
        </Card>);
}
